import {stringify} from "comment-json";
import {omit, startCase} from "lodash";
import {makeNodeFromJs, NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {
  convertJsonSchemaToShorthandIfPossible
} from "../../../json/schema/convert-json-schema-to-shorthand-if-possible";
import {nodeLabel, removeDefaultService} from "../../default-service";
import {ProophBoardDescriptionKeys} from "../../descriptions";
import {getNextGeo} from "../graph-manipulation";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PlayCommandRuntimeInfo} from "./command-definitions";
import {prepareDiffCommand} from "./prepare-diff-command";

export class CommandAdded implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'NewCommand';
  public readonly operation: PlayshotChangedOperation = 'added';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.command;
  public readonly diff: PlayshotChangedDiff;

  private newCommand: PlayCommandRuntimeInfo;

  constructor(name: string, nodeId: string, command: PlayCommandRuntimeInfo, playshot: Playshot) {

    const rules = playshot.playConfig.commandHandlers[command.desc.name] || [];

    this.name = name;
    this.nodeId = nodeId;
    this.diff = {
      new: prepareDiffCommand(command, rules),
      old: null,
    };
    this.newCommand = command;
  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = graph.getNode(this.nodeId);

    if(existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the command seems to exist already on the board.`,
        details: `There is at least an element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }

    const rules = playshot.playConfig.commandHandlers[this.name] || [];

    const node = makeNodeFromJs({
      id: this.nodeId,
      type: NodeType.command,
      name: nodeLabel(this.name),
      metadata: stringify(omit({
        ...this.newCommand.desc,
        schema: convertJsonSchemaToShorthandIfPossible(this.newCommand.schema),
        uiSchema: this.newCommand.uiSchema,
        rules,
        // @TODO: handle factory
      }, [
        "name",
        ...ProophBoardDescriptionKeys
      ]))
    })

    graph.addNode(node);
    graph.switchParent(node, syncContext, getNextGeo(addedNodes), true);
    graph.focusNode(node);

    addedNodes[node.getId()] = graph.getNode(node.getId()) || node;

    return makeAsyncIterable({
      cody: `I've added a command card for "${nodeLabel(this.name)}" to the board`
    });
  }
}
